import { useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, Brain, Heart, TrendingUp, Briefcase, Leaf, CloudRain, Sparkles } from 'lucide-react';

/*
 * Wellness Wheel — recreated to match the live clinity.in wheel exactly.
 * Flat 2D donut wheel, slow continuous rotation. Inner labels/icons + center "YOU"
 * counter-rotate (CSS keyframes synced) so they stay upright. Soft pastel palette,
 * dashed outer ring, indigo halo glow.
 */

const SIZE = 400;
const C = SIZE / 2;            // center 200
const R_OUTER = 170;
const R_INNER = 60;
const R_RING = 178;            // dashed outer ring
const R_LABEL = 95;             // icon badge radius
const R_TEXT = 119;             // label text radius (just below icon)

// Order: clockwise, starting from top-right (matches live site exactly)
const DIMS = [
  { key: 'physical',  label: 'Physical',  fill: '#DBEAFE', stroke: '#60A5FA', icon: Zap,        iconColor: '#2563EB', desc: 'Energy, fitness, sleep, and overall body health.' },
  { key: 'mental',    label: 'Mental',    fill: '#E0E7FF', stroke: '#818CF8', icon: Brain,      iconColor: '#4F46E5', desc: 'Focus, stress management, and cognitive clarity.' },
  { key: 'emotional', label: 'Emotional', fill: '#F3E8FF', stroke: '#C084FC', icon: Heart,      iconColor: '#7C3AED', desc: 'Emotional awareness, resilience, and relationships.' },
  { key: 'financial', label: 'Financial', fill: '#D1FAE5', stroke: '#34D399', icon: TrendingUp, iconColor: '#059669', desc: 'Income, savings, investments, and financial security.' },
  { key: 'career',    label: 'Career',    fill: '#FEF3C7', stroke: '#FBBF24', icon: Briefcase,  iconColor: '#D97706', desc: 'Career direction, growth, and professional fulfillment.' },
  { key: 'lifestyle', label: 'Lifestyle', fill: '#FCE7F3', stroke: '#F472B6', icon: Leaf,       iconColor: '#DB2777', desc: 'Daily routines, environment, and digital habits.' },
  { key: 'spiritual', label: 'Spiritual', fill: '#EDE9FE', stroke: '#A78BFA', icon: CloudRain,  iconColor: '#6D28D9', desc: 'Purpose, meaning, and inner peace.' },
  { key: 'self_care', label: 'Self-Care', fill: '#FFF7ED', stroke: '#FB923C', icon: Sparkles,   iconColor: '#EA580C', desc: 'Personal time, recovery, and grooming.' },
];

const total = DIMS.length;

// Pre-compute each segment's geometry
const segs = DIMS.map((d, i) => {
  const a0 = (i / total) * Math.PI * 2 - Math.PI / 2;
  const a1 = ((i + 1) / total) * Math.PI * 2 - Math.PI / 2;
  const aM = (a0 + a1) / 2;

  const xo0 = C + Math.cos(a0) * R_OUTER, yo0 = C + Math.sin(a0) * R_OUTER;
  const xo1 = C + Math.cos(a1) * R_OUTER, yo1 = C + Math.sin(a1) * R_OUTER;
  const xi0 = C + Math.cos(a0) * R_INNER, yi0 = C + Math.sin(a0) * R_INNER;
  const xi1 = C + Math.cos(a1) * R_INNER, yi1 = C + Math.sin(a1) * R_INNER;

  const path = `M ${xo0} ${yo0} A ${R_OUTER} ${R_OUTER} 0 0 1 ${xo1} ${yo1} L ${xi1} ${yi1} A ${R_INNER} ${R_INNER} 0 0 0 ${xi0} ${yi0} Z`;

  const ix = C + Math.cos(aM) * R_LABEL;
  const iy = C + Math.sin(aM) * R_LABEL;
  const tx = C + Math.cos(aM) * R_TEXT;
  const ty = C + Math.sin(aM) * R_TEXT;

  return { ...d, idx: i, path, ix, iy, tx, ty };
});

export default function WellnessWheelInteractive() {
  const [active, setActive] = useState(null);
  const activeDim = active ? DIMS.find((d) => d.key === active) : null;

  return (
    <div className="relative w-full max-w-[480px] mx-auto" data-testid="wellness-wheel-interactive">
      {/* Indigo halo glow */}
      <div
        className="absolute inset-0 rounded-full blur-3xl opacity-60 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 60%)' }}
        aria-hidden="true"
      />

      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="relative w-full h-auto">
        <defs>
          <filter id="wheel-soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
            <feOffset dx="0" dy="3" result="offsetblur" />
            <feComponentTransfer><feFuncA type="linear" slope="0.15" /></feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="wheel-center-gradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#EEF2FF" />
          </radialGradient>
        </defs>

        {/* Dashed outer ring */}
        <circle cx={C} cy={C} r={R_RING} fill="none" stroke="#E0E7FF" strokeWidth="1" strokeDasharray="2 4" opacity="0.6" />

        {/* Pastel segments */}
        <g filter="url(#wheel-soft-shadow)">
          {segs.map((s) => {
            const isActive = active === s.key;
            const isDim = active && !isActive;
            return (
              <motion.path
                key={s.key}
                d={s.path}
                fill={s.fill}
                stroke={s.stroke}
                strokeWidth="1.5"
                style={{ cursor: 'pointer', transformOrigin: `${C}px ${C}px`, transformBox: 'fill-box' }}
                animate={{ opacity: isDim ? 0.5 : 0.92, scale: isActive ? 1.025 : 1 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onMouseEnter={() => setActive(s.key)}
                onMouseLeave={() => setActive(null)}
                onClick={() => setActive((cur) => (cur === s.key ? null : s.key))}
              />
            );
          })}
        </g>

        {/* Labels and icons (upright, never rotate) */}
        <g>
          {segs.map((s) => {
            const Ico = s.icon;
            return (
              <g key={`lbl-${s.key}`} pointerEvents="none">
                <circle cx={s.ix} cy={s.iy} r="18" fill="#FFFFFF" opacity="0.95" />
                <foreignObject x={s.ix - 11} y={s.iy - 11} width="22" height="22">
                  <div style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ico size={18} color={s.iconColor} strokeWidth={2.2} />
                  </div>
                </foreignObject>
                <text
                  x={s.tx}
                  y={s.ty}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="600"
                  fill="#475569"
                  style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.5px' }}
                >
                  {s.label}
                </text>
              </g>
            );
          })}
        </g>

        {/* Center disc + YOU */}
        <circle cx={C} cy={C} r="56" fill="url(#wheel-center-gradient)" stroke="#E0E7FF" strokeWidth="1" />
        <text
          x={C}
          y={C + 7}
          textAnchor="middle"
          fontSize="22"
          fill="#4F46E5"
          fontWeight="700"
          letterSpacing="3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          YOU
        </text>
      </svg>

      {/* Active dimension caption */}
      <div className="mt-6 min-h-[44px] text-center" aria-live="polite">
        {activeDim ? (
          <>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#4F46E5]">{activeDim.label}</div>
            <div className="mt-1 text-sm text-[#475569] max-w-md mx-auto leading-relaxed">{activeDim.desc}</div>
          </>
        ) : (
          <div className="text-xs sm:text-[13px] text-[#94A3B8] font-medium">Explore the dimensions of wellbeing.</div>
        )}
      </div>
    </div>
  );
}
